import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translations
import en from './locales/en.json';
import hi from './locales/hi.json';
import fr from './locales/fr.json';
import ar from './locales/ar.json';
import es from './locales/es.json';

// Build namespace map for a locale file.
// Every namespace used by any component via useTranslation('ns') MUST appear
// here, otherwise i18next returns the raw key instead of the translated string.
// Fall back to {} so i18next never receives undefined as a namespace value.
const makeLocaleEntry = (loc) => ({
  translation:          loc                         || {},
  adminPortal:          loc?.adminPortal             || {},
  cloudDocs:            loc?.cloudDocs               || {},
  ueba:                 loc?.ueba                   || {},
  useCases:             loc?.useCases               || {},
  featuresSection:      loc?.featuresSection         || {},
  howItWorks:           loc?.howItWorks             || {},
  testimonials:         loc?.testimonials           || {},
  solutionsPage:        loc?.solutionsPage           || {},
  partnerPage:          loc?.partnerPage             || {},
  toolsPage:            loc?.toolsPage               || {},
  deploymentGuide:      loc?.deploymentGuide         || {},
  aiCompliance:         loc?.aiCompliance            || {},
  chartWidget:          loc?.chartWidget             || {},
  addCategory:          loc?.addCategory             || {},
  licenseMgmt:          loc?.licenseMgmt             || {},
  nlAutomation:         loc?.nlAutomation            || {},
  pendingConsent:       loc?.pendingConsent           || {},
  productivityAnalytics:loc?.productivityAnalytics   || {},
  screenshotMonitoring: loc?.screenshotMonitoring    || {},
  sessionMetrics:       loc?.sessionMetrics          || {},
  usbDomain:            loc?.usbDomain               || {},
});

const resources = {
  en: makeLocaleEntry(en),
  hi: makeLocaleEntry(hi),
  fr: makeLocaleEntry(fr),
  ar: makeLocaleEntry(ar),
  es: makeLocaleEntry(es),
};

// ---------------------------------------------------------------------------
// Helper: read the current logged-in user's ID from localStorage.
// This is available immediately at module load time (written by AuthContext on
// login) and is used to scope the language preference key so that User A's
// choice never leaks into User B's session on the same browser.
// ---------------------------------------------------------------------------
const getCurrentUserId = () => {
  try {
    const stored = localStorage.getItem('currentUser');
    if (!stored) return null;
    const user = JSON.parse(stored);
    return user?.user_id || user?.email || null;
  } catch {
    return null;
  }
};

// Returns the localStorage key used to store this user's language preference.
// Public so SettingsContext / AccountSettings can read/write the same key.
export const getUserLangKey = (userId) =>
  userId ? `userLangPref_${userId}` : null;

i18n
  // No LanguageDetector — SettingsContext is the single source of truth.
  // We do an optimistic pre-load below (using the user-scoped key) to avoid a
  // flash of English before SettingsContext finishes its API call.
  .use(initReactI18next)
  .init({
    resources,
    // Start with English; overridden immediately by the optimistic pre-load
    // below and then authoritatively by SettingsContext after its API call.
    lng: 'en',
    fallbackLng: 'en',
    // Explicit string (not array) so react-i18next's nsOrContext check resolves
    // to a predictable string and avoids array-vs-string ambiguity in the
    // unstableNamespaces memo inside useTranslation.
    defaultNS: 'translation',
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    // react-i18next reads i18n.options.react inside useTranslation's useMemo via a
    // (non-null-safe) object spread. Without this block options.react is undefined,
    // which crashes the whole app ("Cannot convert undefined or null to object").
    // useSuspense:false because there is no <Suspense> boundary around translations.
    react: {
      useSuspense: false,
    },
  });

// ---------------------------------------------------------------------------
// Apply direction helper (shared by changeLanguage and resetLanguage).
// ---------------------------------------------------------------------------
const applyDirection = (lang) => {
  if (lang === 'ar') {
    document.documentElement.setAttribute('dir', 'rtl');
    document.documentElement.setAttribute('lang', 'ar');
  } else {
    document.documentElement.setAttribute('dir', 'ltr');
    document.documentElement.setAttribute('lang', lang || 'en');
  }
};

// ---------------------------------------------------------------------------
// Optimistic pre-load: apply this user's stored language preference before
// React mounts so there is no visible flash of English.
// ---------------------------------------------------------------------------
const _startupUserId = getCurrentUserId();
const _startupKey = getUserLangKey(_startupUserId);
const _startupLang = (_startupKey && localStorage.getItem(_startupKey)) || null;
if (_startupLang) {
  i18n.changeLanguage(_startupLang);
  applyDirection(_startupLang);
}

// ---------------------------------------------------------------------------
// changeLanguage — the single function used by SettingsContext & components.
// Writes to a USER-SCOPED key so no user can affect another user's preference.
// ---------------------------------------------------------------------------
export const changeLanguage = (lang) => {
  if (!lang) return;
  i18n.changeLanguage(lang);
  applyDirection(lang);

  // Persist under the current user's scoped key only.
  const userId = getCurrentUserId();
  const key = getUserLangKey(userId);
  if (key) {
    localStorage.setItem(key, lang);
  }
};

// ---------------------------------------------------------------------------
// resetLanguage — called on logout. Resets the UI to English WITHOUT writing
// to the current user's scoped preference (their saved choice is preserved for
// their next login). Also removes the legacy unscoped key if it exists.
// ---------------------------------------------------------------------------
export const resetLanguage = () => {
  i18n.changeLanguage('en');
  applyDirection('en');
  // Clean up legacy unscoped keys written by the old implementation.
  localStorage.removeItem('userLangPref');
  localStorage.removeItem('i18nextLng');
};

export default i18n;

